import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Secciones/Header';
import Footer from '../components/Secciones/Footer';
import { UserAuthContext } from '../UserAuthContext';
import { getProductImageUrl, useProductImageFallback } from '../utils/productImages';
import NotFound from '../assets/images/NotFound.png';
import './Orders.css';

const API_BASE = 'https://valleyapi-dr9y.onrender.com';

const money = (value) => (Number(value) || 0).toLocaleString('en-US', {
  style: 'currency',
  currency: 'USD'
});

const formatDate = (value) => {
  if (!value) return 'Not scheduled';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Not scheduled'
    : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const statusClass = (status = '') => status.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export default function Orders() {
  const { user, loading: authLoading } = useContext(UserAuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const selectedSort = query.get('sort') || 'date_desc';

  const [orders, setOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/signup', { replace: true, state: { returnTo: '/orders' } });
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (authLoading || !user) return;
    const controller = new AbortController();

    async function loadOrders() {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('user_token');
        if (!token) throw new Error('Your session expired.');

        const url = new URL(`${API_BASE}/api/cart/orders`);
        if (selectedSort !== 'default') url.searchParams.set('sort', selectedSort);
        const response = await axios.get(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });

        setOrders((Array.isArray(response.data) ? response.data : []).map((order) => ({
          ...order,
          total: Number.parseFloat(order.total) || 0,
          subtotal: Number.parseFloat(order.subtotal) || 0,
          shipping_cost: Number.parseFloat(order.shipping_cost) || 0,
          taxes: Number.parseFloat(order.taxes) || 0,
          items: Array.isArray(order.items) ? order.items.map((item) => ({
            ...item,
            product_price: Number.parseFloat(item.product_price) || 0,
            product_image: getProductImageUrl(item.product_image)
          })) : []
        })));
      } catch (requestError) {
        if (requestError.name !== 'CanceledError') {
          console.error('Unable to load orders:', requestError);
          setError(
            requestError.response?.status === 401
              ? 'Your session expired. Please log in again.'
              : 'Your orders could not be loaded. Please try again.'
          );
        }
      } finally {
        setLoading(false);
      }
    }

    loadOrders();
    return () => controller.abort();
  }, [user, authLoading, selectedSort]);

  const changeSort = (event) => {
    const next = new URLSearchParams(location.search);
    next.set('sort', event.target.value);
    navigate(`/orders?${next.toString()}`);
  };

  const pendingCount = orders.filter((order) => order.order_state === 'Waiting for Payment').length;
  const activeCount = orders.filter((order) => ['Processing', 'Quote Requested'].includes(order.order_state)).length;

  return (
    <div className="or-page">
      <Header />
      <main className="or-main">
        <section className="or-hero">
          <div>
            <span className="or-eyebrow">Account center</span>
            <h1>{user?.name ? `${user.name.split(' ')[0]}’s orders.` : 'Your orders.'}</h1>
            <p>Track every request, review product details and complete pending payments from one place.</p>
          </div>
          <Link to="/search?q=*&inStock=true&page=1" className="or-shop-link">
            Continue shopping <span>↗</span>
          </Link>
        </section>

        {location.state?.successMessage && (
          <div className="or-success" role="status">
            <i className="fas fa-check-circle" />
            <span>{location.state.successMessage}</span>
            <button type="button" onClick={() => navigate(location.pathname + location.search, { replace: true })}>×</button>
          </div>
        )}

        {authLoading || loading ? (
          <div className="or-state">
            <span className="or-spinner" />
            <h2>Loading your order history…</h2>
          </div>
        ) : error ? (
          <div className="or-state">
            <span className="or-state-icon"><i className="fas fa-exclamation-circle" /></span>
            <h2>Orders unavailable</h2>
            <p>{error}</p>
            <button type="button" onClick={() => window.location.reload()}>Try again</button>
          </div>
        ) : orders.length === 0 ? (
          <div className="or-empty">
            <img src={NotFound} alt="" />
            <span className="or-eyebrow">No orders yet</span>
            <h2>Your next project can start here.</h2>
            <p>Browse available products, add the parts you need and create your first order.</p>
            <Link to="/search?q=*&inStock=true&page=1">Browse in-stock products <span>→</span></Link>
          </div>
        ) : (
          <>
            <section className="or-overview">
              <div><span>Total orders</span><strong>{orders.length}</strong><i className="fas fa-file-invoice" /></div>
              <div><span>Awaiting payment</span><strong>{pendingCount}</strong><i className="fas fa-credit-card" /></div>
              <div><span>Active orders</span><strong>{activeCount}</strong><i className="fas fa-box-open" /></div>
              <label>
                <span>Sort orders</span>
                <select value={selectedSort} onChange={changeSort}>
                  <option value="date_desc">Newest first</option>
                  <option value="date_asc">Oldest first</option>
                  <option value="total_desc">Total: high to low</option>
                  <option value="total_asc">Total: low to high</option>
                </select>
              </label>
            </section>

            <section className="or-list" aria-label="Order history">
              {orders.map((order) => {
                const expanded = expandedOrder === order.idorders;
                const status = order.order_state || 'Order created';
                return (
                  <article className={`or-order ${expanded ? 'is-expanded' : ''}`} key={order.idorders}>
                    <div className="or-order-top">
                      <div className="or-order-number">
                        <span>Order</span>
                        <h2>#{order.idorders}</h2>
                      </div>
                      <div className="or-order-meta">
                        <div><span>Created</span><strong>{formatDate(order.date)}</strong></div>
                        <div><span>Delivery</span><strong>{formatDate(order.delivery_time)}</strong></div>
                        <div><span>Total</span><strong>{money(order.total)}</strong></div>
                      </div>
                      <span className={`or-status or-status-${statusClass(status)}`}>{status}</span>
                    </div>

                    <div className="or-order-bottom">
                      <div className="or-order-preview">
                        {order.items.slice(0, 3).map((item) => (
                          <div className="or-preview-image" key={item.idorder_item}>
                            <img
                              src={item.product_image}
                              alt=""
                              onError={useProductImageFallback}
                            />
                          </div>
                        ))}
                        <span>{order.items.length} {order.items.length === 1 ? 'item' : 'items'}</span>
                      </div>
                      <div className="or-actions">
                        <button type="button" onClick={() => setExpandedOrder(expanded ? null : order.idorders)}>
                          {expanded ? 'Hide products' : 'Quick view'} <span>{expanded ? '↑' : '↓'}</span>
                        </button>
                        <Link to={`/orders/${order.idorders}`}>View details <span>↗</span></Link>
                        {status === 'Waiting for Payment' && (
                          <button className="or-pay-action" type="button" onClick={() => navigate(`/checkout/${order.idorders}`)}>
                            Complete payment <span>→</span>
                          </button>
                        )}
                      </div>
                    </div>

                    {expanded && (
                      <div className="or-expanded-items">
                        <div className="or-expanded-heading"><span>Products in this order</span><strong>{order.items.length}</strong></div>
                        {order.items.length ? order.items.map((item) => (
                          <div className="or-line-item" key={item.idorder_item}>
                            <div className="or-line-image">
                              <img
                                src={item.product_image}
                                alt={item.product_name}
                                onError={useProductImageFallback}
                              />
                            </div>
                            <div>
                              <span>{item.product_brand || 'Industrial automation'}</span>
                              <h3>{item.product_name}</h3>
                              <p>Quantity {item.quantity}{item.More_Details ? ` · ${item.More_Details}` : ''}</p>
                            </div>
                            <strong>{money(item.product_price * item.quantity)}</strong>
                          </div>
                        )) : <p className="or-no-items">No products were returned for this order.</p>}
                      </div>
                    )}
                  </article>
                );
              })}
            </section>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
